import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React, { useMemo } from 'react';
import { __t } from '../../utils/t';
import { Icon } from '../Icon';
import { usePagination, DOTS } from './usePagination';
import { useIsMobile } from '../../hooks';

import './styles.scss';

interface Props {
    currentPage: number;
    totalCount: number;
    perPageValue?: number;
    perPageOptions?: number[];
    paginationHandler: (offset: number) => void;
    perPageChangeHandler?: (value: number) => void;
}

export const Pagination = ({
    currentPage,
    totalCount,
    perPageValue,
    perPageOptions,
    paginationHandler,
    perPageChangeHandler,
}: Props) => {
    const isMobile = useIsMobile();

    const paginationRange = usePagination({
        currentPage,
        totalCount,
        siblingCount: isMobile ? 0 : 1,
        pageSize: perPageValue ? perPageValue : 20,
    });

    const pages = useMemo(() => {
        if (!paginationRange) {
            return null;
        }

        return paginationRange.map((i, index) => {
            if (i === DOTS) {
                return (
                    <li key={index} className='app-pagination__button app-pagination__button--dot'>
                        &#8230;
                    </li>
                );
            }

            return (
                <li
                    className={`app-pagination__button ${currentPage === i ? 'app-pagination__button--active' : ''}`}
                    key={index}
                    onClick={() => paginationHandler(i as number)}
                >
                    {i}
                </li>
            );
        });
    }, [paginationRange]);

    const perPageSelector = useMemo(() => {
        if (!perPageValue || !perPageOptions || !perPageChangeHandler) {
            return null;
        }

        const handleChange = (event: SelectChangeEvent<string>) => {
            perPageChangeHandler(Number(event.target.value));
        };

        return (
            <div className='app-pagination__per-page'>
                <span className='app-pagination__per-page-title'>{__t('Показувати по')}</span>
                <Select
                    className='app-pagination-per-page-select'
                    value={perPageValue.toString()}
                    onChange={handleChange}
                    variant='standard'
                    disableUnderline
                    MenuProps={{
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                        classes: {
                            paper: 'app-pagination-per-page-select__dropdown',
                            list: 'app-pagination-per-page-select__list',
                        },
                    }}
                    IconComponent={(props) => <Icon icon='chevron-down-regular' size='14px' {...props} />}
                >
                    {perPageOptions.map((i) => (
                        <MenuItem key={i} className='app-pagination-per-page-select__item' value={i.toString()}>
                            {i}
                        </MenuItem>
                    ))}
                </Select>
            </div>
        );
    }, [perPageValue, perPageOptions, perPageChangeHandler]);

    return (
        <div
            className={`app-pagination ${
                !!perPageValue && !!perPageOptions && !!perPageChangeHandler ? 'app-pagination--min-height' : ''
            }`}
        >
            {paginationRange && paginationRange.length > 1 ? (
                <ul className='app-pagination__wrap'>
                    <li
                        className={`app-pagination__button ${
                            currentPage === 1 ? 'app-pagination__button--disabled' : ''
                        }`}
                        onClick={() => paginationHandler(currentPage - 1)}
                    >
                        <Icon icon='chevron-left-light' />
                    </li>
                    {pages}
                    <li
                        className={`app-pagination__button ${
                            paginationRange && currentPage === paginationRange.length
                                ? 'app-pagination__button--disabled'
                                : ''
                        }`}
                        onClick={() => paginationHandler(currentPage + 1)}
                    >
                        <Icon icon='chevron-right-light' />
                    </li>
                </ul>
            ) : null}

            {perPageSelector}
        </div>
    );
};
